import { Injectable, Injector } from '@angular/core';
import { Router } from '@angular/router';
import { PermissionsModuleAccessLevel } from '@predictiveindex/client-permissions-lib';
import {
    GlobalSearchResult,
    SEARCH_RESULT_TOKEN
} from '@predictiveindex/pi-client-global-search-lib';
import {
    TapestrySidenavService,
    TapestryNotificationService
} from '@predictiveindex/tapestry';
import {
    ModuleUserAccessLevel,
    PI,
    UserAccessSoftwareModule
} from '../../constants-types';
import {
    ModuleName,
    ModuleUserInviteRequest,
    OrgMember,
    UsersToInvite
} from '../../core-data-model';
import { GlobalSearchPersonSnapshotComponent } from '../../core-ui';
import {
    ApiService,
    FeatureFlags,
    FeatureFlagsService,
    InitDataService,
    Logger,
    ModuleUserAccessService,
    UserProfileSettingsService
} from '../../system-services';
import { TranslationService } from '../../translations';
import { ModuleUserApi } from '../ModuleUser';
import { UserApi } from '../UserApi.connect';
import { WaitingIndicatorDecorator } from '../WaitingIndicator.decorator';

@Injectable({ providedIn: 'root' })
export class OrgMemberService {
    loggedInUserOrgMember!: OrgMember;
    private orgApiPrefix = '/api/v5';
    private Inspire2BaseRoute = 'inspire/client';
    private PersonRouteModifier = '/person/';

    constructor(
        private readonly api: ApiService,
        private readonly userProfile: UserProfileSettingsService,
        private readonly initData: InitDataService,
        private readonly tapestryNotification: TapestryNotificationService,
        private readonly TR: TranslationService,
        private readonly moduleUserApi: ModuleUserApi,
        private readonly logger: Logger,
        private readonly router: Router,
        private readonly sideNav: TapestrySidenavService,
        private readonly featureFlagsService: FeatureFlagsService,
        private readonly userApi: UserApi,
        private readonly moduleUserAccess: ModuleUserAccessService
    ) {}

    @WaitingIndicatorDecorator.blockScreenOnWait()
    public async canViewInInspire(orgMemberId: string): Promise<boolean> {
        const orgMember = await this.getOrgMember(orgMemberId);

        const hasReferenceProfile = !(
            orgMember?.referencePatternInfo?.patternType === null ||
            orgMember?.referencePatternInfo?.patternType === undefined
        );
        const hasModuleAccess =
            this.moduleUserAccess.hasModuleAccessLevelOrAbove(
                ModuleName.Inspire,
                PermissionsModuleAccessLevel.LimitedAccess
            );
        const enableWorkshopAccess = this.featureFlagsService.getByFlag(
            FeatureFlags.enableWorkshopAccess
        );

        return hasReferenceProfile && hasModuleAccess && enableWorkshopAccess;
    }

    async getAllOrgMembers(
        companyId: string = this.userProfile.currentCompany,
        includeArchived?: boolean,
        includePending?: boolean
    ): Promise<OrgMember[]> {
        const params: Record<string, boolean> = {};
        if (includeArchived !== undefined && includeArchived !== null) {
            params.includeArchived = includeArchived;
        }
        if (includePending !== undefined && includePending !== null) {
            params.includePending = includePending;
        }

        const [orgMembers] = await this.api.get<
            OrgMember[],
            Record<string, boolean>
        >(
            `${this.orgApiPrefix}/companies/${companyId}/orgmembers`,
            params,
            null,
            PI.appSettings.orgApiUrl
        );
        return orgMembers;
    }

    async getOrgMember(
        personId: string,
        companyId: string = this.userProfile.currentCompany
    ): Promise<OrgMember> {
        const [orgMember] = await this.api.get<OrgMember>(
            `${this.orgApiPrefix}/companies/${companyId}/orgmembers/${personId}`,
            null,
            null,
            PI.appSettings.orgApiUrl
        );
        return orgMember;
    }

    async getLoggedinUserOrgMember(): Promise<OrgMember> {
        if (!this.loggedInUserOrgMember) {
            const personId = this.initData.user.surveyUserId;
            // Fetch OrgMember only if logged in User is associated to Survey User
            if (personId) {
                const companyId = this.initData.user.companyId; // get the default company id of the logged in user
                const [orgMember] = await this.api.get<OrgMember>(
                    `${this.orgApiPrefix}/companies/${companyId}/orgmembers/${personId}`,
                    null,
                    null,
                    PI.appSettings.orgApiUrl
                );
                this.loggedInUserOrgMember = orgMember;
            }
        }
        return this.loggedInUserOrgMember;
    }

    async sendInspireInvite(
        newInspireUsers: UsersToInvite[]
    ): Promise<boolean> {
        const loggedInUser = this.initData.user;
        const moduleUserInviteRequest: ModuleUserInviteRequest = {
            usersToInvite: newInspireUsers,
            softwareModule: UserAccessSoftwareModule.Inspire,
            moduleAccessLevel: ModuleUserAccessLevel.Limited,
            companyName: loggedInUser.company,
            senderFirstName: loggedInUser.firstName,
            senderLastName: loggedInUser.lastName
        };
        try {
            await this.moduleUserApi.inviteUserAccess(moduleUserInviteRequest);
            this.showUserAccessMessage('orgManagement.sendInvitation.success');
            this.logger.logInfo(`OrgMemberService - Inspire Invite Success`, {
                userId: loggedInUser.userId,
                email: loggedInUser.email,
                companyId: loggedInUser.companyId,
                usersToInvite: newInspireUsers
            });
            return true;
        } catch (error) {
            this.showUserAccessMessage('orgManagement.sendInvitation.error');
            this.logger.logError('OrgMemberService - Inspire Invite Error', {
                error: error,
                userId: loggedInUser.userId,
                email: loggedInUser.email,
                companyId: loggedInUser.companyId,
                usersToInvite: newInspireUsers
            });
        }
        return false;
    }

    showUserAccessMessage(message: string) {
        this.tapestryNotification.create({
            message: this.TR.key(message),
            duration: 3000
        });
    }

    /**
     * Handles propper routing for the clicking of an org member
     *
     * @param assessmentUserId accepts a surveyUserId of a user
     * @param firstName optional accepts the first name of the user
     * @param fullName optional accepts the full name of the user
     * @param hasCompletedAssessment optional accepts a boolean value to determine if the user has completed the assessment
     */
    async routeOrgMemberClick(
        assessmentUserId: string,
        firstName?: string,
        fullName?: string,
        hasCompletedAssessment?: boolean
    ) {
        const loggedInOrgMember = await this.getLoggedinUserOrgMember();
        const hasLoggedInUserCompletedAssessment =
            loggedInOrgMember?.referencePatternInfo?.patternType != null;
        if (
            (!hasCompletedAssessment || !hasLoggedInUserCompletedAssessment) &&
            this.userProfile.isUserLoggedInDefaultCompany()
        ) {
            return this.openSnapshot(assessmentUserId, firstName, fullName);
        }

        const canViewInInspire = await this.canViewInInspire(assessmentUserId);

        if (canViewInInspire) {
            await this.router.navigate([
                `${this.Inspire2BaseRoute}${this.PersonRouteModifier}${assessmentUserId}`
            ]);
        } else {
            return this.openSnapshot(assessmentUserId, firstName, fullName);
        }
    }

    closeSidenav() {
        this.sideNav.close();
    }

    private openSnapshot(
        assessmentUserId: string,
        firstName: string,
        fullName: string
    ) {
        this.sideNav.open({
            component: GlobalSearchPersonSnapshotComponent,
            title: '',
            injector: this.generateSnapshotInjector(
                assessmentUserId,
                firstName,
                fullName
            )
        });
    }

    private generateSnapshotInjector(
        assessmentUserId: string,
        firstName?: string,
        fullName?: string
    ): Injector {
        // Note: need to transform to a search result to make it compatible with snapshot
        const partialSearchResult = new GlobalSearchResult({
            id: assessmentUserId,
            firstName: firstName,
            displayName: fullName
        });
        return Injector.create({
            providers: [
                {
                    provide: SEARCH_RESULT_TOKEN,
                    useValue: partialSearchResult
                }
            ]
        });
    }

    private async isCurrentUserAThirdPartyUser() {
        try {
            const thirdPartyUsers = await this.userApi.getUsers(
                1000,
                null,
                'thirdParty'
            );
            return thirdPartyUsers.some(
                (user) => user.id === this.initData.user.userGuid
            );
        } catch (error) {
            this.logger.logError('Error fetching third party users', error);
        }
    }
}
